import { eq } from "drizzle-orm";
import { type PowerSyncDrizzleDB, subtask, todo, type Todo } from "@cartero/powersync";
import { computeNextDueDate, nowISO } from "../utils/date-helpers";
import { computeReminderAt, deriveReminderOffsetMinutes } from "./scheduling";
import { generateId } from "@fleet/utils";

/**
 * Creates a new Todo.
 */
export async function createTodoMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  text: string,
  opts?: {
    listId?: string;
    important?: boolean;
    dueDate?: string;
    myDayDate?: string;
    note?: string;
    recurrenceRule?: string;
  },
): Promise<string> {
  const id = generateId();
  const now = nowISO();
  await db.insert(todo).values({
    id,
    user_id: userId,
    list_id: opts?.listId ?? null,
    text: text.trim(),
    completed: 0,
    important: opts?.important ? 1 : 0,
    note: opts?.note ?? null,
    due_date: opts?.dueDate ?? null,
    my_day_date: opts?.myDayDate ?? null,
    recurrence_rule: opts?.recurrenceRule ?? null,
    created_at: now,
    updated_at: now,
  });
  return id;
}

/**
 * Updates an existing Todo.
 */
export async function updateTodoMutation(
  db: PowerSyncDrizzleDB,
  id: string,
  updates: Partial<{
    text: string | null;
    completed: number | null;
    important: number | null;
    note: string | null;
    due_date: string | null;
    recurrence_rule: string | null;
    my_day_date: string | null;
    list_id: string | null;
    reminder_at: string | null;
  }>,
): Promise<void> {
  const finalUpdates = { ...updates };
  if (finalUpdates.text !== undefined && finalUpdates.text !== null) {
    finalUpdates.text = finalUpdates.text.trim();
  }

  await db
    .update(todo)
    .set({ ...finalUpdates, updated_at: nowISO() })
    .where(eq(todo.id, id));
}

/**
 * Toggles a Todo's completion status. Handles recurrence if necessary.
 */
export async function toggleTodoMutation(
  db: PowerSyncDrizzleDB,
  id: string,
  currentCompleted: boolean,
  currentTodo?: Todo,
): Promise<void> {
  const completing = !currentCompleted;

  const recurrenceRule = currentTodo?.recurrence_rule;
  // Only spawn the next occurrence when there is a concrete due date to advance
  // from; a recurrence rule without a due date has no anchor and would loop on
  // `new Date()`, so we treat it as a plain completion.
  if (completing && recurrenceRule && currentTodo.due_date) {
    const nextDue = computeNextDueDate(currentTodo.due_date, recurrenceRule);
    const now = nowISO();

    // `computeNextDueDate` returns null once the recurrence hits its UNTIL bound;
    // in that case we just complete the task without scheduling a successor.
    if (nextDue === null) {
      await db.update(todo).set({ completed: 1, updated_at: now }).where(eq(todo.id, id));
      return;
    }

    // Carry the relative reminder over to the next occurrence so a "10 min
    // before" reminder keeps firing on every instance, re-anchored to the new
    // due date.
    const reminderOffset = deriveReminderOffsetMinutes(
      currentTodo.due_date,
      currentTodo.reminder_at,
    );
    const nextReminderAt =
      reminderOffset === null ? null : computeReminderAt(nextDue, reminderOffset);
    const newId = generateId();

    // Complete the current occurrence and spawn the next one atomically, so a
    // reader never observes a recurring task that is neither pending nor has a
    // successor.
    await db.transaction(async (trx) => {
      await trx.update(todo).set({ completed: 1, updated_at: now }).where(eq(todo.id, id));

      await trx.insert(todo).values({
        id: newId,
        user_id: currentTodo.user_id,
        list_id: currentTodo.list_id ?? null,
        text: currentTodo.text,
        completed: 0,
        important: currentTodo.important,
        note: currentTodo.note ?? null,
        due_date: nextDue,
        reminder_at: nextReminderAt,
        my_day_date: null,
        recurrence_rule: recurrenceRule,
        created_at: now,
        updated_at: now,
      });
    });
  } else {
    await db
      .update(todo)
      .set({ completed: completing ? 1 : 0, updated_at: nowISO() })
      .where(eq(todo.id, id));
  }
}

/**
 * Toggles a Todo's important status.
 */
export async function toggleImportantMutation(
  db: PowerSyncDrizzleDB,
  id: string,
  currentImportant: boolean,
): Promise<void> {
  await db
    .update(todo)
    .set({ important: currentImportant ? 0 : 1, updated_at: nowISO() })
    .where(eq(todo.id, id));
}

/**
 * Adds or removes a Todo from My Day.
 */
export async function addToMyDayMutation(
  db: PowerSyncDrizzleDB,
  id: string,
  include: boolean,
): Promise<void> {
  await db
    .update(todo)
    .set({
      my_day_date: include ? nowISO() : null,
      updated_at: nowISO(),
    })
    .where(eq(todo.id, id));
}

/**
 * Soft-deletes a Todo (trash, ACTIVITY-CORE L2): marks `deleted_at` and normal
 * views filter it out. Subtasks remain intact for restore.
 * Permanent deletion is `purgeTodoMutation`.
 */
export async function deleteTodoMutation(db: PowerSyncDrizzleDB, id: string): Promise<void> {
  const now = nowISO();
  await db.update(todo).set({ deleted_at: now, updated_at: now }).where(eq(todo.id, id));
}

/**
 * Restores a task from the trash (the server records it as `restored`).
 *
 * §4.11: It is deliberately NOT wrapped in `patchRowVerified`. `/trash` does
 * call this mutation (a route linked from the sidebar), but no live route
 * creates/soft-deletes tasks (`createTodoMutation`, `updateTodoMutation`,
 * `toggleTodoMutation`, `deleteTodoMutation`… have no consumer in
 * `apps/web`—the template's task dashboard was removed, see
 * `apps/web/src/routes/_app/index.tsx`) so the trash can never have a row to
 * restore. Hardening this would protect dead code (see the `malla` case: 33
 * protections over a domain with no consumers). Recommendation recorded for
 * the owner: purge the inherited `todo`/`list` domain from `listto-app`, rather
 * than wiring it up.
 */
export async function restoreTodoMutation(db: PowerSyncDrizzleDB, id: string): Promise<void> {
  await db.update(todo).set({ deleted_at: null, updated_at: nowISO() }).where(eq(todo.id, id));
}

/**
 * PERMANENT purge of a task (and its subtasks) from the trash.
 * Irreversible on the client; the server retains the snapshot in `activity`.
 */
export async function purgeTodoMutation(db: PowerSyncDrizzleDB, id: string): Promise<void> {
  await db.transaction(async (trx) => {
    await trx.delete(subtask).where(eq(subtask.todo_id, id));
    await trx.delete(todo).where(eq(todo.id, id));
  });
}
